import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Mail,
  IdCard,
  Copy,
  Power,
  User,
  MailCheck,
  PhoneCall,
  MessageCircleCodeIcon,
  PhoneCallIcon,
  MessageCircle,
  BadgeCheck,
  Plus,
  TimerIcon,
  Calendar,
  FileText,
  User2,
  ALargeSmall,
  Send,
  Slack,
  TicketX,
  GlassesIcon,
} from "lucide-react";
import { useApiCall } from "../../../../../component/custom-hooks/api-call";
import InnerLoader from "../../../../../component/ui/loader/inner-loader";
import { Alert, Snackbar } from "@mui/material";
import CommentsSection from "../../../../../component/comment";
import ConvertLeadToDeal from "../../../../../component/modals/convert-lead/convert-lead-to-deal";
import AddActivity from "../../../../../component/modals/add-activity";
import { PopupTimeline } from "../../../../../component/ui/timeline";
import { FieldRow} from "../../../../../component/Popup-comp";
import LeadStatusUpdate from "../lead-status-popup";
import { iconsMap } from "../../../../../component/icons/inner-pages-icons/icons";
import Icon from "../../../../../component/icons/inner-pages-icons";

const tabs = [
  { key: "timeline", label: "Timeline", icon: <TimerIcon size={14} /> },
  { key: "summary", label: "Summary", icon: <User size={14} /> },
  // { key: "about", label: "About", icon: <FileTextIcon size={14} />, },
  {
    key: "comments",
    label: "Comments",
    icon: <MessageCircleCodeIcon size={14} />,
  },
  { key: "call", label: "Calls", icon: <PhoneCallIcon size={14} /> },
  { key: "emails", label: "Emails", icon: <Mail size={14} /> },
  { key: "sms", label: "Sms", icon: <MessageCircle size={14} /> },
];

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function Stat({ title, value, icon: Icon, hint, tone = "default" }) {
  return (
    <div className="group relative rounded-2xl border border-solid border-slate-200 bg-white p-4 shadow-card ">
      <div className="flex items-start gap-3">
        <div className="min-w-0">
          <div className="text-xs text-slate-500 capitalize">
            {title.includes("_")
              ? title.split("_").map((word) => ` ${word}`)
              : title}
          </div>

          <div className="text-xs font-semibold tracking-tight">{value}</div>
          {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
        </div>
      </div>
    </div>
  );
}


const STATUS_TONES = {
  "new lead": "blue",
  attempted: "indigo",
  voicemail: "sky",
  "no answer update 2": "grey",
  "wrong number": "red",
  "call back": "amber",
  interested: "green",
  "not interested": "red",
  "closed won": "green",
  "closed lost": "red",
  "do not contact": "neutral",
  duplicate: "violet",
  active: "violet",
  pending: "pending",
};
function Badge({ children, status, icon: Icon }) {
  const tones = {
    default: "bg-slate-100 text-slate-700",
    green: "bg-emerald-100 text-emerald-700",
    blue: "bg-blue-100 text-blue-700",
    amber: "bg-[#fffbeb] text-[#78350f]",
    red: "bg-rose-100 text-rose-700",
    grey: "bg-neutral-100 text-neutral-700",
    gray: "bg-gray-100 text-gray-800",
    yellow: "bg-yellow-100 text-yellow-700",
    orange: "bg-orange-100 text-orange-700",
    violet: "bg-violet-100 text-violet-700",
    neutral: "bg-gray-50 text-gray-700 border border-gray-200",
    pink: "bg-pink-200 text-pink-700",
    teal: "bg-teal-200 text-teal-700",
    indigo: "bg-indigo-200 text-indigo-700",
    sky: "bg-sky-200 text-sky-700",
    lime: "bg-lime-200 text-lime-700",
    pending: "bg-[#FFF4E5] text-[#FB8C00]",
  };

  // Get tone dynamically from status
  const tone = STATUS_TONES[status] || "default";

  return (
    <span
      className={classNames(
        "inline-flex items-center gap-1 rounded-[6px] px-[9px] py-[3px] text-[11px] font-medium",
        tones[tone],
      )}
    >
      {Icon ? <Icon size={14} /> : null}
      {children}
    </span>
  );
}



export default function LeadQuickView({
  open = true,
  onClose = () => { },
  onError = () => { },
  onSuccess = () => { },
  data,
  ProductList,
  PackageList = [],
  DealStages,
  SubProductList,
  fetchSubProducts = () => { },
  fetchPackages = () => { },
  onStatusUpdate = () => { },
}) {
  const [lead, setLead] = useState({});
  const [activeTab, setActiveTab] = useState("timeline");
  const [status, setStatus] = useState(lead?.leadstatus);
  const [statusCode, setStatusCode] = useState(lead?.status_code);
  const containerRef = useRef(null);
  const apiCall = useApiCall();
  const [isLoading, setIsLoading] = useState(false);
  const [usersList, setUsers] = useState([]);

  const [showActivityModal, setShowActivityModal] = useState(false);

  const [showConvertModal, setShowConvertModal] = useState(false);
  const [statusList, setStatusList] = useState([]);
  const [convertFormData, setConvertFormData] = useState({
    deal_product: "",
    deal_package: "",
    estimated_payment_date: "",
    dealamount: "",
    dealstage: "",
    dealprobability: "",
    description: "",
  });
  const GetData = async (id) => {
    let apiData = JSON.stringify({
      leadid: id,
    });

    try {
      let call = await apiCall({
        method: "get",
        url: `leads/get-lead/${id}`,
        data: apiData,
      });
      let leadData = call?.data;

      setIsLoading(true);

      let activity = leadData?.recent_activity?.map((item) => {
        const dateObj = new Date(item.time);
        const formattedDate = dateObj
          .toISOString()
          .slice(0, 16)
          .replace("T", " ");

        return {
          ts: formattedDate,
          text: item.data,
        };
      });

      // console.log("leadData?.schedules", leadData?.schedules)


      const sdata = {
        id: leadData?.leadid,
        name: leadData?.name,
        probability: leadData["probability(%)"],
        phone: leadData?.phone,
        email: leadData?.email,
        service: leadData?.product_name,
        type: `${leadData?.leadtype?.charAt(0)?.toUpperCase()}${leadData?.leadtype?.slice(1)}`,
        LeadSource: leadData?.leadsource,
        AssignedTo: leadData?.assigned_user_details ?? "-",
        Subject: leadData?.subject,
        Message: leadData?.message,
        LeadStatus: leadData?.leadstatus,
        CreatedTime: leadData?.createdtime,
        stats: leadData?.stats,
        updated_at: leadData?.updated_at,
        country: leadData?.country,
        state: leadData?.state,
        city: leadData?.city,
        brand: leadData?.lead_brand?.brandname,
        agent: leadData?.assigned_user_details,
        activities: leadData?.activities,
        metas: leadData?.meta,
        deal_product: leadData?.service,
        lead_brand: leadData?.lead_brand,
        status_code: leadData?.status_code
      };

      // console.log("sdata", sdata)
      // console.log("lead.activities", lead.activities)

      setStatus(leadData?.leadstatus);
      setStatusCode(leadData?.status_code);
      setLead(sdata);
      setIsLoading(true);
    } catch (error) {
      console.warn("Error : ", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await apiCall({
        method: "get",
        url: `users/agents-list`,
      });

      console.log("Users API Response ===>", res);
      const newUsers = res?.agents || res?.customers || res?.data || [];
      setUsers(newUsers);
    } catch (err) {
      console.error("Error fetching users", err);
    } finally {
    }
  };

  const getLeadStatus = async () => {
    try {
      const res = await apiCall({
        method: "get",
        url: "get-dropdown-list",
      });
      console.log("res?.data", res?.data);

      setStatusList(res?.data?.lead_status || []);
    } catch (error) {
      console.warn("Error fetching Package:", error);
    }
  };

  useEffect(() => {
    fetchUsers();
    getLeadStatus();
  }, []);

  const handleConvertLead = async (id, formData) => {
    try {
      let response = await apiCall({
        method: "post",
        url: `leads/convert-lead/${id}`,
        data: {
          name: lead?.name,
          ...formData,
        },
      });

      if (response?.success) {
        alert("Lead successfully converted to contact ✅");
      } else {
        alert("Failed to convert lead");
      }
    } catch (error) {
      console.error("Error converting lead:", error);
      alert("Something went wrong");
    }
  };

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      if (e.key.toLowerCase() === "f" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        const el = document.getElementById("quickview-search");
        el?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  
  const [alertData, setAlertData] = useState({ isShow: false });

  const ConvertDeal = () => {
    setShowConvertModal(true);
  };

  const handleClose = () => {
    setAlertData({ isShow: false });
  };

  const onActivitySuccess = (message) => {
    setAlertData({ isShow: true, type: "success", message: message });
    GetData(lead?.id);
    setShowActivityModal(false);
  };

  const handleSuccess = (message) => {
    if (onSuccess) onSuccess(message); // ✅ call parent prop
    setShowConvertModal(false);
  };

  useEffect(() => {
    if (activeTab === "timeline") {
      GetData(data?.leadid);
    }
  }, [activeTab, data]);

 

  const T = {
    bg: "#EDE8E1",
    sidebar: "#1a1726",
    sidebarText: "rgba(255,255,255,0.45)",
    sidebarActive: "#FFFFFF",
    sidebarActiveBg: "rgba(99,102,241,0.15)",
    sidebarHover: "rgba(255,255,255,0.05)",
    sidebarAccent: "#818cf8",
    card: "#FFFFFF",
    cardBorder: "rgba(0,0,0,0.10)",
    cardHover: "#FAF8F6",
    text: "#1a1520",
    textSecondary: "#5c5466",
    textMuted: "#908892",
    border: "rgba(0,0,0,0.08)",
    accent: "#6366f1",
    accentLight: "#EEF0FF",
    accentText: "#4F46E5",
    inputBg: "#F3EFE9",
    inputBorder: "rgba(0,0,0,0.1)",
    modalBg: "#FFFFFF",
    rowHover: "#F5F1EC",
    selectedRow: "#EEEAFF",
    selectedBorder: "#6366f1",
    shadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
    shadowLg: "0 8px 30px rgba(0,0,0,0.08)",
  };

  const css = {
    app: {
      display: "flex",
      height: "100vh",
      fontFamily: "'DM Sans', -apple-system, sans-serif",
      background: T.bg,
      color: T.text,
      overflow: "hidden",
    },
    sidebar: {
      width: 250,
      background: T.sidebar,
      display: "flex",
      flexDirection: "column",
      flexShrink: 0,
      overflow: "hidden",
    },
    main: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
    card: {
      background: T.card,
      border: `1px solid ${T.cardBorder}`,
      borderRadius: 12,
      padding: 16,
      boxShadow: T.shadow,
    },
    btn: {
      padding: "7px 14px",
      borderRadius: 8,
      border: `1px solid ${T.border}`,
      background: T.card,
      color: T.textSecondary,
      cursor: "pointer",
      fontSize: 13,
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontFamily: "inherit",
      transition: "all 0.15s",
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    },
    btnPrimary: {
      padding: "8px 18px",
      borderRadius: 8,
      border: "none",
      background: "linear-gradient(92deg,#0057FB 1.54%,#003495 114.12%)",
      color: "#fff",
      cursor: "pointer",
      fontSize: 13,
      fontWeight: 600,
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontFamily: "inherit",
      boxShadow: "0 2px 10px rgba(99,102,241,0.3)",
    },
    input: {
      background: T.inputBg,
      border: `1px solid ${T.inputBorder}`,
      borderRadius: 8,
      padding: "9px 12px",
      color: T.text,
      fontSize: 13,
      outline: "none",
      fontFamily: "inherit",
      width: "100%",
    },
    select: {
      background: T.inputBg,
      border: `1px solid ${T.inputBorder}`,
      borderRadius: 8,
      padding: "6px 12px",
      color: T.text,
      fontSize: 13,
      outline: "none",
      fontFamily: "inherit",
      cursor: "pointer",
    },
    badge: (color) => ({
      display: "inline-flex",
      alignItems: "center",
      padding: "3px 9px",
      borderRadius: 6,
      fontSize: 11,
      fontWeight: 600,
      background: color + "14",
      color: color,
      letterSpacing: "0.3px",
    }),
    pill: (active) => ({
      padding: "5px 12px",
      borderRadius: 20,
      fontSize: 12,
      fontWeight: active ? 600 : 400,
      border: active
        ? "1px solid rgba(99,102,241,0.35)"
        : `1px solid ${T.border}`,
      background: active ? T.accentLight : T.card,
      color: active ? T.accentText : T.textMuted,
      cursor: "pointer",
      fontFamily: "inherit",
      boxShadow: active ? "none" : "0 1px 2px rgba(0,0,0,0.03)",
    }),
  };


  const activityIcon = (type) =>
    ({
      call: "phone",
      phone: "phone",
      email: "mail",
      sms: "sms",
      consultation: "consultation",
      default: "default",

      note: "note",
      meeting: "calendar",
      "follow-up": "calendar",
      followup: "calendar",
      stage: "arrowRight",
      voicemail: "mic",
      chat: "sms",
      comment: "sms",
      demo: "demo",
      messasge: "sms",
      presentation: "presentation",
      assign: "assign",
      unassign: "unassign",
      file: "file",
      brief: "brief",
    })[type] || "check";
  const activityColor = (type) =>
    ({
      call: "#3b82f6", // blue
      email: "#8b5cf6", // violet
      sms: "#06b6d4", // cyan
      consultation: "#f59e0b", // amber
      note: "#f59e0b", // amber
      meeting: "#10b981", // emerald
      stage: "#6366f1", // indigo
      "follow-up": "#6366f1", // indigo
      voicemail: "#ef4444", // red

      chat: "#0ea5e9", // sky blue
      presentation: "#14b8a6", // teal
      demo: "#a855f7", // purple
      default: "#a855f7", // purple
      messasge: "#f43f5e", // rose

      // remaining icons (added)
      phone: "#3b82f6", // blue
      comment: "#0ea5e9", // sky
      followup: "#6366f1", // indigo
      assign: "#22c55e", // green
      unassign: "#ef4444", // red
      file: "#eab308", // slate
      brief: "#eab308", // yellow
    })[type] || "#6b7280";

  ////////////// fake data for calls //////////////
  const refreshLead = {
    id: lead?.id,
    leadName: lead?.name,
    phone: lead?.phone,
    activities: [
      // {
      //   id: 1,
      //   type: "call",
      //   summary: "Initial Discussion Call",
      //   date: "2024-07-01T10:30:00",
      //   agent: "Hendry",
      //   duration: "12m 45s",
      //   details:
      //     "Discussed product requirements and pricing structure. Client showed interest in premium plan.",
      // },
      // {
      //   id: 2,
      //   type: "voicemail",
      //   summary: "Follow-up Voicemail",
      //   date: "2024-07-03T03:15:00",
      //   agent: "Hendry",
      //   duration: "45s",
      //   details:
      //     "Left a voicemail regarding proposal approval and next meeting schedule.",
      // },
      // {
      //   id: 3,
      //   type: "call",
      //   summary: "Product Demo Call",
      //   date: "2024-07-06T01:00:00",
      //   agent: "Theresa",
      //   duration: "28m 10s",
      //   details:
      //     "Provided live demo of CRM dashboard and analytics features. Client asked about integration options.",
      // },
    ],
  };

  // const I = (name, size = 16) => (icons[name] ? icons[name](size) : null);

  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  const fmtTime = (iso) =>
    new Date(iso).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  const fmtDateTime = (iso) => `${fmtDate(iso)} ${fmtTime(iso)}`;

  // const icons = {
  //   search: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <circle cx="11" cy="11" r="8" />
  //       <path d="m21 21-4.3-4.3" />
  //     </svg>
  //   ),
  //   check: (s = 16) => (
  //     <svg
  //       xmlns="http://www.w3.org/2000/svg"
  //       width="24"
  //       height="24"
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       stroke-width="2"
  //       stroke-linecap="round"
  //       stroke-linejoin="round"
  //       class="lucide lucide-check-check-icon lucide-check-check"
  //     >
  //       <path d="M18 6 7 17l-5-5" />
  //       <path d="m22 10-7.5 7.5L13 16" />
  //     </svg>
  //   ),
  //   phone: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  //     </svg>
  //   ),
  //   mail: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <rect width="20" height="16" x="2" y="4" rx="2" />
  //       <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  //     </svg>
  //   ),
  //   msg: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22z" />
  //     </svg>
  //   ),
  //   user: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
  //       <circle cx="12" cy="7" r="4" />
  //     </svg>
  //   ),
  //   users: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
  //       <circle cx="9" cy="7" r="4" />
  //       <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
  //       <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  //     </svg>
  //   ),
  //   plus: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <path d="M5 12h14" />
  //       <path d="M12 5v14" />
  //     </svg>
  //   ),
  //   clock: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <circle cx="12" cy="12" r="10" />
  //       <polyline points="12 6 12 12 16 14" />
  //     </svg>
  //   ),
  //   dollar: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <line x1="12" x2="12" y1="2" y2="22" />
  //       <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  //     </svg>
  //   ),
  //   chart: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <path d="M3 3v18h18" />
  //       <path d="m19 9-5 5-4-4-3 3" />
  //     </svg>
  //   ),
  //   bell: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
  //       <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  //     </svg>
  //   ),
  //   chevDown: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <path d="m6 9 6 6 6-6" />
  //     </svg>
  //   ),
  //   chevRight: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <path d="m9 18 6-6-6-6" />
  //     </svg>
  //   ),
  //   x: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <path d="M18 6 6 18" />
  //       <path d="m6 6 12 12" />
  //     </svg>
  //   ),
  //   check: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <path d="M20 6 9 17l-5-5" />
  //     </svg>
  //   ),
  //   edit: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
  //     </svg>
  //   ),
  //   trash: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <path d="M3 6h18" />
  //       <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
  //       <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
  //     </svg>
  //   ),
  //   file: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
  //       <path d="M14 2v4a2 2 0 0 0 2 2h4" />
  //     </svg>
  //   ),
  //   star: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="currentColor"
  //       stroke="currentColor"
  //       strokeWidth="1"
  //     >
  //       <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  //     </svg>
  //   ),
  //   filter: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  //     </svg>
  //   ),
  //   grid: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <rect width="7" height="7" x="3" y="3" rx="1" />
  //       <rect width="7" height="7" x="14" y="3" rx="1" />
  //       <rect width="7" height="7" x="14" y="14" rx="1" />
  //       <rect width="7" height="7" x="3" y="14" rx="1" />
  //     </svg>
  //   ),
  //   list: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <line x1="8" x2="21" y1="6" y2="6" />
  //       <line x1="8" x2="21" y1="12" y2="12" />
  //       <line x1="8" x2="21" y1="18" y2="18" />
  //       <line x1="3" x2="3.01" y1="6" y2="6" />
  //       <line x1="3" x2="3.01" y1="12" y2="12" />
  //       <line x1="3" x2="3.01" y1="18" y2="18" />
  //     </svg>
  //   ),
  //   target: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <circle cx="12" cy="12" r="10" />
  //       <circle cx="12" cy="12" r="6" />
  //       <circle cx="12" cy="12" r="2" />
  //     </svg>
  //   ),
  //   refresh: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
  //       <path d="M21 3v5h-5" />
  //       <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
  //       <path d="M8 16H3v5" />
  //     </svg>
  //   ),
  //   tag: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" />
  //       <circle cx="7.5" cy="7.5" r=".5" fill="currentColor" />
  //     </svg>
  //   ),
  //   play: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="currentColor"
  //       stroke="none"
  //     >
  //       <polygon points="6 3 20 12 6 21 6 3" />
  //     </svg>
  //   ),
  //   pause: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="currentColor"
  //       stroke="none"
  //     >
  //       <rect x="6" y="4" width="4" height="16" />
  //       <rect x="14" y="4" width="4" height="16" />
  //     </svg>
  //   ),
  //   mic: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
  //       <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
  //       <line x1="12" x2="12" y1="19" y2="22" />
  //     </svg>
  //   ),
  //   send: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <path d="m22 2-7 20-4-9-9-4Z" />
  //       <path d="m22 2-11 11" />
  //     </svg>
  //   ),
  //   home: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  //       <polyline points="9 22 9 12 15 12 15 22" />
  //     </svg>
  //   ),
  //   settings: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
  //       <circle cx="12" cy="12" r="3" />
  //     </svg>
  //   ),
  //   arrowUp: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <path d="m5 12 7-7 7 7" />
  //       <path d="M12 19V5" />
  //     </svg>
  //   ),
  //   arrowDown: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <path d="M12 5v14" />
  //       <path d="m19 12-7 7-7-7" />
  //     </svg>
  //   ),
  //   calendar: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <rect width="18" height="18" x="3" y="4" rx="2" />
  //       <line x1="16" x2="16" y1="2" y2="6" />
  //       <line x1="8" x2="8" y1="2" y2="6" />
  //       <line x1="3" x2="21" y1="10" y2="10" />
  //     </svg>
  //   ),
  //   link: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
  //       <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  //     </svg>
  //   ),
  //   box: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
  //       <path d="m3.3 7 8.7 5 8.7-5" />
  //       <path d="M12 22V12" />
  //     </svg>
  //   ),
  //   shield: (s = 16) => (
  //     <svg
  //       width={s}
  //       height={s}
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //     >
  //       <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
  //     </svg>
  //   ),
  //   default: (p) => (
  //     //     <svg {...p}>
  //     //   <circle cx="12" cy="12" r="3" />
  //     //   <path d="M12 3v3" />
  //     //   <path d="M12 18v3" />
  //     //   <path d="M3 12h3" />
  //     //   <path d="M18 12h3" />
  //     // </svg>
  //     <svg {...p}>
  //       <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  //     </svg>
  //   ),
  // };

  





  const [statusOpen, setStatusOpen] = useState(false);

  const dropdownRef = useRef(null);

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 !pr-0 z-[60] flex items-stretch justify-end bg-slate-900/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.aside
              ref={containerRef}
              initial={{ x: 40, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 40, opacity: 0 }}
              transition={{ type: "spring", stiffness: 380, damping: 32 }}
              className="relative mt-0 mr-0 mb-auto ml-0 flex h-full p-[28px] pb-0 w-[800px] max-w-[98vw] flex-col overflow-hidden rounded-3xl rounded-r-none  border border-solid border-slate-200 bg-[#ffffff] shadow-xl"
              aria-modal
              role="dialog"
            >
              {isLoading ? (
                <>
                  {/* Header */}
                  <div className=" bg-white/90  backdrop-blur supports-[backdrop-filter]:bg-white/70 w-full z-[999]">
                    <div className="flex flex-wrap items-center justify-between gap-3 w-full pb-4">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex items-center justify-center size-12  bg-gradient-to-br from-indigo-500/20 to-emerald-500/20 rounded-full overflow-hidden">
                          <p className="capitalize font-semi-bold text-base text-black">
                            {lead?.name?.split(" ")?.map((n, i) => n.charAt(0))}
                          </p>
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <h2 className="text-xl font-bold text-slate-900">
                              {lead?.name}
                            </h2>
                           
                            <div ref={dropdownRef} className="relative inline-block z-30">
                              {/* Badge */}
                              <div
                                onClick={() => setStatusOpen(true)}
                                className="cursor-pointer"
                              >
                                <Badge
                                  status={status ? status?.toLowerCase() : "-"}
                                  icon={BadgeCheck}
                                >
                                  {status || "-"}
                                </Badge>
                              </div>


                              {/* Dropdown */}
                              {/* {statusOpen && (
                                <div className="absolute mt-2 w-[180px] bg-white border rounded-md shadow-md z-50">
                                  {statusList?.map((s) => (
                                    <div
                                      key={s?.id}
                                      onClick={() => {
                                        updateStatus(s?.id);
                                        setStatusOpen(false);
                                      }}
                                      className="px-3 py-2 text-[13px] hover:bg-gray-100 cursor-pointer"
                                    >
                                      {s?.value}
                                    </div>
                                  ))}
                                </div>
                              )} */}
                            </div>
                          </div>

                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                            <span className="inline-flex items-center gap-1">
                              <MailCheck className="size-4" /> {lead?.email}
                            </span>

                            <span className="inline-flex items-center gap-1">
                              <PhoneCall className="size-4" /> {lead?.phone}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          className="inline-flex items-center gap-1.5 rounded-xl bg-[#0B54FF] px-3 py-2 text-sm font-medium text-white hover:bg-[#0849e0]"
                          onClick={onClose}
                          aria-label="Close"
                        >
                          <X className="size-4" /> Exit
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-hidden pt-0 w-full">
                    {/* Left: main content */}
                    <div className="col-span-12 flex min-h-0 flex-col gap-4">
                      {/* Top stats */}

                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          flexWrap: "wrap",
                          alignItems: "center",
                          justifyContent: "start",
                          textTransform: "capitalize"

                        }}
                      >
                        {lead?.stats?.map(
                          (ele, idx) =>
                            ele?.value !== "-" &&
                            ele?.value !== null && (
                              <div
                                style={{
                                  padding: "8px 14px",
                                  background: "#F3EFE9",
                                  borderRadius: 10,
                                  border: `1px solid rgba(0,0,0,0.08)`,
                                }}
                              >
                                <div style={{ fontSize: 10, color: "#908892" }}>
                                  {ele?.key?.includes("_")
                                    ? ele?.key
                                      ?.split("_")
                                      .map((word) => ` ${word}`)
                                    : ele?.key}
                                </div>
                                <div
                                  style={{
                                    fontSize: 15,
                                    fontWeight: 700,
                                    marginTop: 3,
                                    color: "#1a1520",
                                  }}
                                >
                                  {ele?.value}
                                </div>
                              </div>
                            ),
                        )}

                        {/* <PopupAssigned
                          AssignedUsers={lead?.AssignedTo}
                          client={lead?.id}
                          apiLoadonSuccess={() =>
                            GetData(data?.leadid)
                          }
                          haslead={true}
                        /> */}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 justify-between">



                        <div className="flex flex-wrap items-center gap-2 ">
                          <button
                            className="inline-flex items-center gap-1.5 tracking-normal rounded-lg bg-[linear-gradient(92deg,#0057FB_1.54%,#003495_114.12%)] 
                          hover:bg-[linear-gradient(92deg,#0066FF_1.54%,#0045AA_114.12%)] px-[18px] py-2 text-[13px] font-semibold text-white"
                            onClick={() => setShowActivityModal(true)}
                            aria-label="Close"
                          >
                            <Plus className="size-[14px]" /> Add Follow Up
                          </button>

                          <button
                            className="inline-flex items-center gap-1.5 rounded-lg border border-solid border-[rgba(0,0,0,0.1)] bg-[rgb(243,239,233)]  px-[18px] py-2 text-[13px] font-semibold text-[rgb(26,21,32)] hover:bg-slate-50"
                            onClick={ConvertDeal}
                          >
                            <Power className="size-[14px]" /> Convert to Deal
                          </button>

                          {/* {statusList?.length > 0 ? (
                            <div className="relative w-[200px]">
                              <select
                                onChange={(e) => updateStatus(e.target.value)}
                                disabled={isUpdating}
                                defaultValue={
                                  statusCode.toLowerCase() === "pending"
                                    ? ""
                                    : statusCode
                                }
                                className={` rounded-lg px-3 py-[6px]  text-[13px]  font-inherit  border  outline-none  transition-all duration-200
                              ${isUpdating ? "cursor-not-allowed opacity-70" : "cursor-pointer"}
                              `}
                                style={{
                                  background: "#F3EFE9",
                                  borderColor: "rgba(0,0,0,0.1)",
                                  color: "#1a1520",
                                }}
                              >
              
                                <option value="" disabled>
                                  {isUpdating ? "Updating..." : "Select Status"}
                                </option>

                                {statusList?.map((s) => (
                                  <option key={s?.id} value={String(s?.id)}>
                                    {s?.value}
                                  </option>
                                ))}
                              </select>

                             
                              {isUpdating && (
                                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                  <div className="h-3 w-3 animate-spin rounded-full border-2 border-gray-400 border-t-transparent"></div>
                                </div>
                              )}
                            </div>
                          ) : null} */}
                        </div>
                      </div>



                      {/* Tabs */}
                      <div className="border-y border-solid pt-1 border-slate-200 flex items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-1">
                          {tabs?.map((t) => (
                            <button
                              key={t.key}
                              onClick={() => {
                                setActiveTab(t.key);
                              }}
                              className={classNames(
                                "flex items-center gap-[6px] px-[16px] py-[11px] text-[13px] font-normal border-b-2 transition-all duration-200",
                                activeTab === t.key
                                  ? "border-blue-600 text-blue-600 font-semibold"
                                  : "border-transparent text-slate-500 hover:text-slate-800",
                              )}
                            >
                              {t.icon}
                              {t.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="h-full overflow-auto rounded-2xl py-2">
                        {activeTab === "summary" && (
                          // <div
                          //   style={{
                          //     display: "flex",
                          //     flexDirection: "column",
                          //     gap: 16,
                          //   }}
                          // >
                          //   <div style={css.card} className="flex-1">
                          //     <div
                          //       style={{
                          //         fontSize: 14,
                          //         fontWeight: 600,
                          //         color: T.text,
                          //         marginBottom: 12,
                          //       }}
                          //     >
                          //       Contact Information
                          //     </div>
                          //     <div
                          //       style={{
                          //         display: "grid",
                          //         gridTemplateColumns: "120px 1fr",
                          //         gap: 10,
                          //         fontSize: 13,
                          //       }}
                          //     >
                          //       <span style={{ color: T.textMuted }}>
                          //         Full Name:
                          //       </span>
                          //       <span
                          //         style={{ color: T.text, fontWeight: 500 }}
                          //       >
                          //         {lead.name}
                          //       </span>
                          //       <span style={{ color: T.textMuted }}>
                          //         Service:
                          //       </span>
                          //       <span
                          //         style={{ color: T.text, fontWeight: 500 }}
                          //       >
                          //         {lead.service}
                          //       </span>
                          //       <span style={{ color: T.textMuted }}>
                          //         Email:
                          //       </span>
                          //       <span style={{ color: T.text }}>
                          //         {lead.email}
                          //       </span>
                          //       <span style={{ color: T.textMuted }}>
                          //         Phone:
                          //       </span>
                          //       <span style={{ color: T.text }}>
                          //         {lead.phone}
                          //       </span>
                          //       <span style={{ color: T.textMuted }}>
                          //         Brand:
                          //       </span>
                          //       <span style={{ color: T.text }}>
                          //         {lead.LeadSource}
                          //       </span>
                          //       <span style={{ color: T.textMuted }}>
                          //         Agent:
                          //       </span>
                          //       <span style={{ color: T.text }}>
                          //         {lead?.agent?.length > 0
                          //           ? lead.agent
                          //               .map((user) => user?.name)
                          //               .filter(Boolean)
                          //               .join(", ")
                          //           : "Not Assigned"}
                          //       </span>
                          //       {lead.am && (
                          //         <>
                          //           <span style={{ color: T.textMuted }}>
                          //             Account Manager:
                          //           </span>
                          //           <span style={{ color: T.text }}>
                          //             {lead.am}
                          //           </span>
                          //         </>
                          //       )}
                          //     </div>
                          //   </div>
                          //   <div style={css.card}>
                          //     <div
                          //       style={{
                          //         fontSize: 14,
                          //         fontWeight: 600,
                          //         color: T.text,
                          //         marginBottom: 12,
                          //       }}
                          //     >
                          //       Quick Stats
                          //     </div>
                          //     <div
                          //       style={{
                          //         display: "grid",
                          //         gridTemplateColumns: "repeat(3, 1fr)",
                          //         gap: 12,
                          //       }}
                          //     >
                          //       <div>
                          //         <div
                          //           style={{
                          //             fontSize: 11,
                          //             color: T.textMuted,
                          //             marginBottom: 4,
                          //           }}
                          //         >
                          //           Status
                          //         </div>
                          //         <span
                          //           style={css.badge(
                          //             STATUS_COLORS[status] || "#6b7280",
                          //           )}
                          //         >
                          //           {status}
                          //         </span>
                          //         <Badge
                          //           status={
                          //             status ? status?.toLowerCase() : "-"
                          //           }
                          //           icon={BadgeCheck}
                          //         >
                          //           {status || "-"}
                          //         </Badge>
                          //       </div>
                          //       <div>
                          //         <div
                          //           style={{
                          //             fontSize: 11,
                          //             color: T.textMuted,
                          //             marginBottom: 4,
                          //           }}
                          //         >
                          //           Type
                          //         </div>
                          //         <span
                          //           style={css.badge(
                          //             lead.type == "paid" || "Paid"
                          //               ? "#d97706"
                          //               : "#6b7280",
                          //           )}
                          //         >
                          //           {lead.type}
                          //         </span>
                          //       </div>
                          //       <div>
                          //         {lead.probability && (
                          //           <>
                          //             <div
                          //               style={{
                          //                 fontSize: 11,
                          //                 color: T.textMuted,
                          //                 marginBottom: 4,
                          //               }}
                          //             >
                          //               Probability
                          //             </div>
                          //             <span
                          //               style={css.badge(
                          //                 lead.probability > 70
                          //                   ? "#d97706"
                          //                   : "#6b7280",
                          //               )}
                          //             >
                          //               {lead.probability}
                          //             </span>
                          //           </>
                          //         )}
                          //       </div>
                          //       {lead.health && (
                          //         <div>
                          //           <div
                          //             style={{
                          //               fontSize: 11,
                          //               color: T.textMuted,
                          //               marginBottom: 4,
                          //             }}
                          //           >
                          //             Health
                          //           </div>
                          //           <span
                          //             style={{
                          //               fontSize: 13,
                          //               fontWeight: 600,
                          //               color: HEALTH_COLORS[lead.health],
                          //             }}
                          //           >
                          //             {lead.health} ({lead.healthScore})
                          //           </span>
                          //         </div>
                          //       )}
                          //     </div>
                          //   </div>
                          // </div>

                          <>
                            <div>
                              <div
                                style={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: T.text,
                                  marginBottom: 12,
                                }}
                              >
                                Account Information
                              </div>

                              <div className="grid gap-6 md:grid-cols-2 w-full px-[10px]">
                                <FieldRow
                                  label="Full Name"
                                  value={lead.name}
                                  LabelIcon={ALargeSmall}
                                />
                                <FieldRow
                                  label="Service"
                                  value={lead.service}
                                  LabelIcon={Send}
                                />

                                <FieldRow
                                  label="Email"
                                  value={lead.email}
                                  LabelIcon={Send}
                                  isCapitalize={false}
                                  isCopyable={true}
                                />

                                <FieldRow
                                  label="Phone"
                                  value={lead.phone}
                                  LabelIcon={PhoneCall}
                                />
                                <FieldRow
                                  label="Account Manager"
                                  value={lead.am}
                                  LabelIcon={User2}
                                />


                                <FieldRow
                                  label="Brand Name"
                                  value={lead?.lead_brand?.brandname}
                                  LabelIcon={Slack}
                                  isLink={true}
                                  LinkValue={lead?.lead_brand?.brandurl}
                                />

                                <FieldRow
                                  label="Type"
                                  value={lead?.type}
                                  LabelIcon={TicketX}
                                />

                                <FieldRow
                                  label="Priority"
                                  value={lead?.probability}
                                  LabelIcon={GlassesIcon}
                                />
                                {/* <FieldRow
                                  label="Date"
                                  value={contact?.signupdate}
                                  LabelIcon={CalendarSync}
                                /> */}
                              </div>
                            </div>
                          </>
                        )}

                        {activeTab === "comments" && (
                          <CommentsSection
                            type="lead"
                            id={lead?.id}
                            apiBaseUrl="lead"
                            setAlertData={setAlertData}
                          />
                        )}

                        {activeTab === "timeline" && (
                          // <div className="px-2 w-full h-full rounded-md">
                          //   {lead?.activities?.map((act, i) => {
                          //     const config =
                          //       activityConfig[act.type] || activityConfig.note;

                          //     return (
                          //       <div
                          //         key={act.id}
                          //         style={{
                          //           display: "flex",
                          //           gap: 12,
                          //           marginBottom: 16,
                          //         }}
                          //       >
                          //         <div
                          //           style={{
                          //             display: "flex",
                          //             flexDirection: "column",
                          //             alignItems: "center",
                          //           }}
                          //         >
                          //           <div
                          //             style={{
                          //               width: 34,
                          //               height: 34,
                          //               borderRadius: "50%",
                          //               background:
                          //                 activityColor(
                          //                   act?.icon?.toLowerCase(),
                          //                 ) + "12",
                          //               display: "flex",
                          //               alignItems: "center",
                          //               justifyContent: "center",
                          //               color: activityColor(
                          //                 act?.icon?.toLowerCase(),
                          //               ),
                          //               flexShrink: 0,
                          //             }}
                          //           >
                          //             {I(
                          //               activityIcon(act?.icon?.toLowerCase()),
                          //               15,
                          //             )}
                          //           </div>
                          //           {i < lead.activities.length - 1 && (
                          //             <div
                          //               style={{
                          //                 width: 1,
                          //                 flex: 1,
                          //                 background: T.border,
                          //                 marginTop: 4,
                          //               }}
                          //             />
                          //           )}
                          //         </div>
                          //         <div style={{ flex: 1, paddingBottom: 4 }}>
                          //           <div
                          //             style={{
                          //               display: "flex",
                          //               justifyContent: "space-between",
                          //               alignItems: "center",
                          //               marginBottom: 2,
                          //             }}
                          //           >
                          //             <span
                          //               style={{
                          //                 fontSize: 13,
                          //                 fontWeight: 600,
                          //                 color: T.text,
                          //               }}
                          //               className="capitalize"
                          //             >
                          //               {act?.logcat}
                          //             </span>
                          //             <span
                          //               style={{
                          //                 fontSize: 11,
                          //                 color: T.textMuted,
                          //               }}
                          //             >
                          //               {act?.logtime}
                          //             </span>
                          //           </div>
                          //           <div
                          //             style={{
                          //               fontSize: 12,
                          //               color: T.textMuted,
                          //               marginBottom: 4,
                          //             }}
                          //           >
                          //             {act?.logtype} · {act?.user?.name} ·{" "}
                          //             {moment(
                          //               act?.logtime,
                          //               "DD-MMM-YYYY hh:mm:ss A",
                          //             ).fromNow()}
                          //           </div>
                          //           <div
                          //             style={{
                          //               fontSize: 12,
                          //               color: T.textSecondary,
                          //               lineHeight: 1.6,
                          //             }}
                          //           >
                          //             {act.log}
                          //           </div>
                          //         </div>
                          //       </div>

                          //     );
                          //   })}
                          //   {lead?.activities?.length === 0 && (
                          //     <div
                          //       style={{
                          //         textAlign: "center",
                          //         color: "#908892",
                          //         padding: 40,
                          //       }}
                          //     >
                          //       No activities logged yet
                          //     </div>
                          //   )}
                          // </div>
                          <PopupTimeline timeline={lead?.activities} />
                        )}

                        {activeTab === "call" && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 12,
                            }}
                          >
                            <div style={css.card}>
                              <div
                                style={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: T.text,
                                  marginBottom: 12,
                                }}
                              >
                                Make a Call
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  marginBottom: 16,
                                }}
                              >
                                <input
                                  style={{ ...css.input, flex: 1 }}
                                  value={refreshLead.phone}
                                  readOnly
                                />
                                <button style={css.btnPrimary}>
                                  {/* {I("phone", 14)} Call Now */}
                                  <Icon  name="phone" size={16} /> Call Now
                                </button>
                              </div>
                            </div>
                            <div
                              style={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: T.text,
                                marginBottom: 8,
                              }}
                            >
                              Call History
                            </div>
                            {refreshLead.activities
                              .filter(
                                (a) =>
                                  a.type === "call" || a.type === "voicemail",
                              )
                              .map((call) => (
                                <div
                                  key={call.id}
                                  style={{
                                    ...css.card,
                                    display: "flex",
                                    gap: 12,
                                  }}
                                >
                                  <div
                                    style={{
                                      width: 34,
                                      height: 34,
                                      borderRadius: "50%",
                                      background:
                                        activityColor(call.type) + "12",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: activityColor(call.type),
                                      flexShrink: 0,
                                    }}
                                  >
                                    {/* {I(activityIcon(call.type), 15)} */}
                                    <Icon name="call" size={15} />
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        marginBottom: 2,
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 13,
                                          fontWeight: 600,
                                          color: T.text,
                                        }}
                                      >
                                        {call.summary}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: 11,
                                          color: T.textMuted,
                                        }}
                                      >
                                        {fmtDateTime(call.date)}
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 12,
                                        color: T.textMuted,
                                        marginBottom: 4,
                                      }}
                                    >
                                      {call.agent} · {call.duration}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 12,
                                        color: T.textSecondary,
                                      }}
                                    >
                                      {call.details}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            {refreshLead.activities.filter(
                              (a) =>
                                a.type === "call" || a.type === "voicemail",
                            ).length === 0 && (
                                <div
                                  style={{
                                    textAlign: "center",
                                    color: T.textMuted,
                                    padding: 40,
                                  }}
                                >
                                  No call history
                                </div>
                              )}
                          </div>
                        )}

                        {activeTab === "emails" && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 12,
                            }}
                          >
                            <div style={css.card}>
                              <div
                                style={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: T.text,
                                  marginBottom: 12,
                                }}
                              >
                                Compose Email
                              </div>
                              <input
                                placeholder="Subject..."
                                style={{ ...css.input, marginBottom: 8 }}
                              />
                              <textarea
                                placeholder="Type your email message..."
                                style={{
                                  ...css.input,
                                  minHeight: 120,
                                  resize: "vertical",
                                  marginBottom: 8,
                                }}
                              />
                              <button style={css.btnPrimary}>
                                {/* {I("send", 14)} Send Email */}
                                <Icon name="send" size={14} /> Send Email
                              </button>
                            </div>
                            <div
                              style={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: T.text,
                                marginBottom: 8,
                              }}
                            >
                              Email History
                            </div>
                            {refreshLead.activities
                              .filter((a) => a.type === "email")
                              .map((email) => (
                                <div key={email.id} style={{ ...css.card }}>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      marginBottom: 6,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: T.text,
                                      }}
                                    >
                                      {email.summary}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 11,
                                        color: T.textMuted,
                                      }}
                                    >
                                      {fmtDateTime(email.date)}
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 12,
                                      color: T.textMuted,
                                      marginBottom: 4,
                                    }}
                                  >
                                    From: {email.agent}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 13,
                                      color: T.textSecondary,
                                      lineHeight: 1.6,
                                    }}
                                  >
                                    {email.details}
                                  </div>
                                </div>
                              ))}
                            {refreshLead.activities.filter(
                              (a) => a.type === "email",
                            ).length === 0 && (
                                <div
                                  style={{
                                    textAlign: "center",
                                    color: T.textMuted,
                                    padding: 40,
                                  }}
                                >
                                  No email history
                                </div>
                              )}
                          </div>
                        )}

                        {activeTab === "sms" && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 12,
                            }}
                          >
                            <div style={css.card}>
                              <div
                                style={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: T.text,
                                  marginBottom: 12,
                                }}
                              >
                                Send SMS
                              </div>
                              <textarea
                                placeholder="Type your message..."
                                style={{
                                  ...css.input,
                                  minHeight: 80,
                                  resize: "vertical",
                                  marginBottom: 8,
                                }}
                              />
                              <button style={css.btnPrimary}>
                                 <Icon  name="send" size={16} /> 
                                 Send SMS
                              </button>
                            </div>
                            <div
                              style={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: T.text,
                                marginBottom: 8,
                              }}
                            >
                              SMS History
                            </div>
                            {refreshLead.activities
                              .filter((a) => a.type === "sms")
                              .map((sms) => (
                                <div key={sms.id} style={{ ...css.card }}>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      marginBottom: 6,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: T.text,
                                      }}
                                    >
                                      {sms.summary}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 11,
                                        color: T.textMuted,
                                      }}
                                    >
                                      {fmtDateTime(sms.date)}
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 12,
                                      color: T.textMuted,
                                      marginBottom: 4,
                                    }}
                                  >
                                    {sms.agent}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 13,
                                      color: T.textSecondary,
                                      lineHeight: 1.6,
                                    }}
                                  >
                                    {sms.details}
                                  </div>
                                </div>
                              ))}
                            {refreshLead.activities.filter(
                              (a) => a.type === "sms",
                            ).length === 0 && (
                                <div
                                  style={{
                                    textAlign: "center",
                                    color: T.textMuted,
                                    padding: 40,
                                  }}
                                >
                                  No SMS history
                                </div>
                              )}
                          </div>
                          // <TabPlaceholder
                          //   icon={MessageSquare}
                          //   title="SMS"
                          //   message="SMS communication history is under development."
                          // />
                        )}
                      </div>
                    </div>

                    {showConvertModal && (
                      <ConvertLeadToDeal
                        leadData={lead}
                        ProductList={ProductList}
                        PackageList={PackageList}
                        SubProductList={SubProductList}
                        fetchSubProducts={fetchSubProducts}
                        fetchPackages={fetchPackages}
                        DealStages={DealStages}
                        onClose={() => {
                          setShowConvertModal(false);
                        }}
                        onError={onError}
                        onSubmit={handleSuccess}
                      />
                    )}


                    {statusOpen && (
                      <LeadStatusUpdate
                        leadData={lead}
                        statusList={statusList}
                        status={status}
                        statusCode={statusCode}
                        onClose={() => setStatusOpen(false)}
                        onSubmit={(msg, newStatus, newStatusCode) => {
                          setStatus(newStatus);
                          setStatusCode(newStatusCode);
                          onStatusUpdate?.(msg);

                        }}
                        onError={onError}
                      />
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between gap-2 border-t border-solid border-slate-200 bg-white p-3 w-full">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <IdCard className="size-4" /> ID {data?.leadid}
                    </div>
                  </div>
                </>
              ) : (
                <InnerLoader wrapperClass={"w-full"} />
              )}
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>

      <Snackbar
        open={alertData?.isShow}
        autoHideDuration={3000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={alertData?.type}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {alertData?.message}
        </Alert>
      </Snackbar>

      {showActivityModal && (
        // <EmployeeDirectoryAddUpdate
        //   onClose={() => {
        //     setShowActivityModal(false);
        //   }}
        //   data={null}
        //   tabIndex={2}
        //   onError={onError}
        //   onSubmit={onActivitySuccess}
        //   id={lead?.id}
        //   entitytype="lead"
        //   entityname={lead.name}
        // />

        <AddActivity
          onSubmit={onActivitySuccess}
          onClose={() => {
            setShowActivityModal(false);
          }}
          onError={onError}
          UsersList={usersList}
          CustomerData={null}
          id={lead?.id}
          entitytype="lead"
          entityname={lead.name}
        />
      )}
    </>
  );
}

function QuickAction({ icon: Icon, label, href, onClick }) {
  const className =
    "inline-flex items-center gap-1.5 rounded-xl border border-solid border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50";
  if (href)
    return (
      <a className={className} href={href}>
        <Icon className="size-4" /> {label}
      </a>
    );
  return (
    <button className={className} onClick={onClick}>
      <Icon className="size-4" /> {label}
    </button>
  );
}

function TabPlaceholder({ icon: Icon, title, message }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center border  border-[rgba(0,0,0,0.10)] rounded-[12px] bg-white">
      <div className="flex items-center justify-center size-14 rounded-full bg-slate-100 mb-4">
        <Icon className="size-6 text-slate-500" />
      </div>
      <h3 className="text-lg font-semibold text-slate-700">{title}</h3>
      <p className="text-sm text-slate-500 mt-1">{message}</p>
      <div className="mt-4 px-4 py-2 text-xs text-slate-400 border border-solid border-slate-200 rounded-full bg-slate-50">
        🚧 Under Development
      </div>
    </div>
  );
}
